import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Flip, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./result.module.css";
import StatusStepper from "./StatusStepper";
import {
  ACTION_REQUIRED_STATUSES,
  TONES,
  displayPhone,
  getAttemptText,
  getDeliveredIn,
  getDateCard,
  getLatest,
  getStatusText,
  getSteps,
  getTone,
  textParts,
} from "./resultState";
import { verifyOtpActionForDelivery, getOTP } from "../../../utils/api";
import { shipperTrack } from "../../../utils/constants/shipperTrack";
import modifyremark from "../../../utils/functions/modifyremark";
import Items from "../Items";
import Otp from "../../common/Otp";
import DeliveryUpdatesModal from "../../../components/DeliveryUpdatesModal";
import { errorCode, track, trackError } from "../../../analytics";
import RiderInfo from "../../../components/atoms/rider-info";
import { launchConfetti } from "../../../utils/functions/confetti";
import { getOtpSession } from "../../../utils/otpSession";

// Tracking result page, ported from the Claude Design file "Tracking Result.dc.html".
// The design's "Preview state" bar is a design tool only and is not built.

const PRE_PICKUP_STATUSES = ["Awb Generated", "Shipment Created", "Pickup Failed"];
const EDIT_STATUSES = [
  "Awb Generated",
  "Picked from Rack",
  "Shipment Created",
  "Packed",
  "Picked-up",
  "Reached Nearest Hub",
  "In-Transit",
  "Reached Destination City",
  "NDR Initiated",
];

const optionsToast = {
  position: toast.POSITION.BOTTOM_CENTER,
  transition: Flip,
  toastClassName: "toast",
  closeButton: false,
};

function censor(str) {
  if (str !== "undefined undefined") {
    return str?.substring(0, 3) + new Array(str?.length - 2).join("*");
  }
  return "*****";
}
function addressphone(str) {
  return str ? `${str.substring(0, 6)}**********` : "**********";
}
const money = (value) => {
  const n = Number(value);
  return value !== undefined && value !== null && value !== "" && !Number.isNaN(n) ? n.toFixed(2) : value ?? "";
};

const TrackingMainScreen = ({ orderData, shipmentstatuses, tripData, ratings }) => {
  const param = useParams();
  const navigate = useNavigate();
  // Funnel events: every button is a tp_cta_click; an OTP wall is a tp_otp_prompted.
  const cta = (name, placement, extra) => track("tp_cta_click", { cta: name, placement, ...extra });
  const otpPrompted = (trigger) => track("tp_otp_prompted", { trigger, prior_session: getOtpSession().valid ? "active" : "none" });
  const tripStatus = tripData?.tripStatus;
  const trackdata = useSelector((store) => store?.awbReducer?.data);
  const trackingDetails = trackdata?.trackingDetails;
  const orgDetails = useSelector((store) => store.companyInfoReducer);
  const cancelationDisabledOrgIds = useSelector((store) => store?.configsReducer?.cancelationDisabledOrgIds) ?? [];

  const [deliveryOtp, setDeliveryOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  // Design: the Delivery OTP is masked until the buyer taps Show OTP; Hide OTP masks it again.
  const [otpRevealed, setOtpRevealed] = useState(false);
  const [itemListModal, setItemListModal] = useState(false);
  const [displayOtpPage, setotpPage] = useState(false);
  const [, setDelivery] = useState(false);
  const [action, setaction] = useState("");
  const [openOrderStatus, setOpenOrderStatus] = useState(false);
  const [displayedValue, setDisplayedValue] = useState(null);
  const [celebrate, setCelebrate] = useState(false);

  const shipment = trackdata?.shipmentDetails;
  const shipmentstatus = shipment?.shipmentstatus;
  const clinetName = trackdata?.companyInfo?.company_name;
  const events = useMemo(() => trackingDetails?.[0]?.tracking ?? [], [trackingDetails]);

  // ---- Steps (shipperTrack picks the step set; timestamps from the first matching event) ----
  const finalArray = useMemo(() => {
    const statusObject = shipperTrack(trackingDetails ? trackingDetails : []);
    return statusObject.map((item) => ({ ...item, timestamp: events.find((e) => e?.shipmentStatus === item?.status)?.timestamp }));
  }, [trackingDetails, events]);

  const tone = getTone(shipmentstatus, shipment?.buyerEDD);
  const T = TONES[tone];
  const { steps, cur, finished } = getSteps(finalArray, tone);
  const dateCard = getDateCard(shipment, { tone, brand: clinetName });
  const statusText = getStatusText(shipmentstatus, tone);
  const attemptText = getAttemptText(shipment);
  const latest = getLatest(events);

  // ---- Alert in the status card ----
  let alert = null;
  if (ACTION_REQUIRED_STATUSES.includes(shipmentstatuses)) {
    const phone = displayPhone(trackdata?.orderDetail?.[0]?.order?.shipping_msisdn);
    const [heading, para, refused] = modifyremark(events[0]?.remarks, phone);
    alert = { title: heading, parts: textParts(para), cta: "Reschedule delivery", canCancel: refused };
  } else if (tone === "delay") {
    alert = { title: "Your shipment is running late", parts: [{ text: "We are trying to get this to you ASAP." }] };
  }

  const isCancellableOrder = !PRE_PICKUP_STATUSES.includes(shipmentstatuses) && !cancelationDisabledOrgIds.includes(orgDetails?.orgID);
  const isDelivered = shipmentstatus === "Delivered";
  const showEditBanner = !isDelivered && (EDIT_STATUSES.includes(shipmentstatuses) || ["ACCEPTED", "STARTED"].includes(tripStatus));
  const isOfd = shipmentstatuses === "Out for Delivery";
  const deliveredIn = isDelivered ? getDeliveredIn(events) : "";

  // Delivered only: when the stepper reaches the last step, show the confetti and the
  // "Delivered in" pill (design: 200 ms after the last step).
  const stopConfetti = React.useRef(null);
  const onCelebrate = useCallback(() => {
    setCelebrate(true);
    stopConfetti.current && stopConfetti.current();
    stopConfetti.current = launchConfetti();
  }, []);
  useEffect(() => () => stopConfetti.current && stopConfetti.current(), []);

  useEffect(() => {
    if (orderData[2] !== null && orderData[2] !== -1) setDisplayedValue(orderData[2]);
    else setDisplayedValue("***");
  }, [orderData]);

  // ---- Actions (unchanged flows) ----
  // Sends the OTP SMS and opens the modal. `trigger` names the button that needed it.
  const getRequest = async (trigger) => {
    const data = await getOTP(shipment?.awbnumber);
    if (data?.status === "Success") {
      otpPrompted(trigger);
      setotpPage(true);
    } else {
      trackError("otp_send", data, { trigger });
      toast.error("Something went wrong!!", optionsToast);
    }
  };

  // placement: "order_details" (Manage order button) or "alert" (failed-delivery banner).
  const relodandNavigate = async (placement = "order_details") => {
    cta(placement === "alert" ? "reschedule_from_alert" : "edit", placement);
    sessionStorage.setItem("showdetails", false);
    if (!sessionStorage.getItem("validateotp")) {
      setaction("Edit");
      await getRequest("edit");
    } else {
      navigate(`/${param.awbInfo}/modifyorder${placement === "alert" ? "?from=alert" : ""}`);
      window.location.reload(true);
    }
  };

  const relodandNavigate2 = async (placement = "alert") => {
    cta(placement === "alert" ? "cancel_from_alert" : "cancel", placement);
    if (!sessionStorage.getItem("validateotp")) {
      setaction("cancel");
      await getRequest("cancel");
    } else {
      navigate(`/${param.awbInfo}/modifyorder?isCancelling=true${placement === "alert" ? "&from=alert" : ""}`, { state: { cancelscreen: true } });
    }
  };

  const handleItems = () => {
    cta("show_items", "details");
    setaction("showProduct");
    if (!sessionStorage.getItem("validateotp")) {
      getRequest("show_items");
    } else {
      // A modal on every screen size (bottom sheet on phones).
      setItemListModal(true);
    }
  };

  const handleCustomerDetail = () => {
    cta("show_details", "details");
    setaction("Edit2");
    if (!sessionStorage.getItem("validateotp")) {
      getRequest("show_details");
    } else {
      navigate(`/${param.awbInfo}/showdetails`);
    }
  };

  const handledeliveryotp = async () => {
    const session = getOtpSession();
    if (!session.valid) {
      setaction("requestotp");
      getRequest("show_otp");
      return;
    }
    // The OTP is stored JSON-encoded; getOtpSession decodes it (sending it raw adds quote
    // characters and the backend rejects it).
    setOtpError("");
    setOtpLoading(true);
    const result = await verifyOtpActionForDelivery(session.otp, param.awbInfo, "delivery-otp");
    setOtpLoading(false);
    if (result?.status === "Success") {
      track("tp_delivery_otp", { result: "shown", via: "session" });
      sessionStorage.setItem("delivery-otp", true);
      setDeliveryOtp(result.result);
      setOtpRevealed(true);
    } else if (result?.code === "429") {
      track("tp_delivery_otp", { result: "rate_limited", via: "session" });
      setOtpError("Too many attempts. Please wait a few minutes and try again.");
    } else {
      track("tp_delivery_otp", { result: "failed", via: "session", code: errorCode(result) });
      setOtpError("We couldn't fetch the OTP right now. Please try again.");
    }
  };

  const closeOrderStatus = useCallback(() => setOpenOrderStatus(false), []);
  const openTrackWindow = () => {
    setOpenOrderStatus(true);
    cta("show_updates", "status_card");
  };

  // The OTP comes from this visit (state) or from the OTP modal (session).
  const shownOtp = deliveryOtp || sessionStorage.getItem("delivery_otp") || "";
  const otpCode = String(shownOtp).replace(/^"(.*)"$/, "$1").trim();
  const otpVisible = otpRevealed && !!otpCode;
  // Four masked boxes until the OTP is known (design default), then one box per digit.
  const otpCells = otpVisible ? otpCode.split("") : Array.from({ length: Math.max(4, otpCode.length) }, () => "");
  const hasRider = !!tripData?.riderProfileUrl;

  // Show: reveal a known OTP, or fetch it (OTP modal or saved session) and reveal when it arrives.
  const toggleOtp = () => {
    if (otpVisible) {
      cta("hide_otp", "kaptaan_card");
      setOtpRevealed(false);
      return;
    }
    cta("show_otp", "kaptaan_card");
    if (otpCode) setOtpRevealed(true);
    else handledeliveryotp();
  };
  const shipping = trackdata?.lastestAddress?.shippingAddress;
  const toneVars = { "--accent": T.accent, "--soft": T.soft, "--border": T.border, "--tail": T.tail };

  return (
    <div className={styles.page} style={toneVars}>
      <div className={styles.top}>
        <div className={styles.edd}>
          <span className={styles.ring1} />
          <span className={styles.ring2} />
          <div className={styles.eddTop}>
            <span className={styles.eddLabel}>{dateCard.label}</span>
            {dateCard.message ? (
              <span className={styles.eddMessage}>{dateCard.message}</span>
            ) : (
              <div className={styles.eddDateRow}>
                <div className={styles.eddDay}>
                  <span className={styles.eddNum}>{dateCard.day}</span>
                  <span className={styles.eddSuffix}>{dateCard.suffix}</span>
                </div>
                <span className={styles.eddMonth}>{dateCard.month}</span>
              </div>
            )}
            {dateCard.pill && <span className={styles.eddPill}>{dateCard.pill}</span>}
            {isDelivered && celebrate && deliveredIn && (
              <span className={styles.tookPill}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                  <path d="M13 2L4 14h7l-1 8 9-12h-7z" />
                </svg>
                Delivered in {deliveredIn}
              </span>
            )}
          </div>
          <div className={styles.eddBottom}>
            <span className={styles.eddBottomLabel}>Order Status</span>
            <span className={styles.eddStatus}>{statusText}</span>
          </div>
        </div>

        <div className={`${styles.card} ${styles.status}`}>
          <div className={styles.statusHead}>
            <div className={styles.statusTitle}>
              <span className={styles.eyebrow}>Order Status</span>
              {attemptText && <span className={styles.attempt}>{attemptText}</span>}
            </div>
            <span className={styles.chip}>
              <span className={styles.chipDot} />
              {statusText}
            </span>
          </div>

          <StatusStepper steps={steps} cur={cur} finished={finished} tone={tone} onCelebrate={isDelivered ? onCelebrate : undefined} />

          {alert && (
            <div className={styles.alert}>
              <div className={styles.alertText}>
                <span className={styles.alertTitle}>{alert.title}</span>
                <span className={styles.alertBody}>
                  {alert.parts.map((p, i) => (p.strong ? <strong key={i}>{p.text}</strong> : <React.Fragment key={i}>{p.text}</React.Fragment>))}
                </span>
              </div>
              {alert.cta && (
                <div className={styles.alertActions}>
                  {alert.canCancel && (
                    <button type="button" className={styles.alertSecondary} onClick={() => relodandNavigate2("alert")}>
                      Cancel Order
                    </button>
                  )}
                  <button type="button" className={styles.alertCta} onClick={() => relodandNavigate("alert")}>
                    {alert.cta}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className={styles.latestRow}>
            <div className={styles.latest}>
              <span className={styles.latestLabel}>Latest update</span>
              <span className={styles.latestValue}>{latest || "—"}</span>
            </div>
            <button type="button" className={styles.pillBtn} onClick={openTrackWindow}>
              Show More Details
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* "Tracking Result.dc.html": one card with the Kaptaan row and, while out for delivery,
          the Delivery OTP under a dashed divider. Each part keeps its own rule: the rider row
          needs the rider's photo from the trip; the OTP shows only when Out for Delivery. */}
      {(hasRider || isOfd) && (
        <div className={styles.kaptaan}>
          {hasRider && <RiderInfo imageUrl={tripData.riderProfileUrl} name={tripData?.riderName} tripId={tripData?.tripId} />}
          {isOfd && (
            <div className={`${styles.otpRow} ${hasRider ? styles.otpRowDivided : ""}`}>
              <div className={styles.otpInfo}>
                <span className={styles.otpIcon} aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7130F0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </span>
                <div className={styles.otpText}>
                  <span className={styles.otpTitle}>Delivery OTP</span>
                  <span className={styles.otpHint}>Please use this OTP while receiving the order.</span>
                  {otpError && (
                    <span className={styles.otpError} role="alert">
                      {otpError}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.otpRight}>
                <div className={styles.otpDigits} role="img" aria-label={otpVisible ? `Delivery OTP ${otpCode.split("").join(" ")}` : "Delivery OTP hidden"}>
                  {otpCells.map((d, i) => (
                    <span key={i} className={`${styles.otpBox} ${otpVisible ? styles.otpBoxOn : ""}`}>
                      {otpVisible ? (
                        <span className={styles.otpDigit} style={{ animationDelay: `${i * 60}ms` }}>
                          {d}
                        </span>
                      ) : (
                        <span className={styles.otpDot} />
                      )}
                    </span>
                  ))}
                </div>
                <button type="button" className={styles.otpBtn} onClick={toggleOtp} disabled={otpLoading} aria-pressed={otpVisible}>
                  {otpVisible ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M3 3l18 18M10.6 5.1A10 10 0 0112 5c6.5 0 10 7 10 7a17 17 0 01-3.2 4.2M6.6 6.6A17 17 0 002 12s3.5 7 10 7a10 10 0 005.4-1.6" />
                      <path d="M9.9 9.9a3 3 0 004.2 4.2" />
                    </svg>
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                  {otpLoading ? "Fetching…" : otpVisible ? "Hide OTP" : "Show OTP"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isOfd && shipment?.trackingLink && (
        <div className={`${styles.card} ${styles.liveFrame}`}>
          <iframe src={shipment.trackingLink} title="tracking link" />
        </div>
      )}

      {isDelivered && ratings}

      <div className={`${styles.card} ${styles.details}`}>
        <div className={styles.section}>
          {/* "Tracking Result.dc.html": Manage order sits in the Order Details header (was a separate yellow card). */}
          <div className={styles.detailsHead}>
            <div className={styles.detailsHeadText}>
              <span className={styles.eyebrow}>Order Details</span>
              {showEditBanner && (
                <span className={styles.detailsHint}>
                  {/* Cancel lives inside Manage Order (Cancel Order tab), with the same rules. */}
                  {isCancellableOrder
                    ? "Reschedule, update personal details, add delivery instructions, or cancel your order."
                    : "Reschedule, update personal details, or add delivery instructions."}
                </span>
              )}
            </div>
            {showEditBanner && (
              <button type="button" className={styles.manageBtn} onClick={() => relodandNavigate("order_details")}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 20h4L19 9l-4-4L4 16z" />
                </svg>
                Manage order
              </button>
            )}
          </div>
          <div className={styles.tiles}>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>Order ID</span>
              <span className={styles.tileValue}>{displayedValue}</span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>Order Value</span>
              <span className={styles.tileValue}>₹ {money(orderData[0])}</span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>Payment Type</span>
              <span className={styles.tileValue}>{trackdata?.orderDetail?.[0]?.order?.paymentType}</span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>No. of Items</span>
              <span className={styles.itemsRow}>
                <span className={styles.tileValue}>{orderData[1] ? orderData[1] : "****"}</span>
                <button type="button" className={styles.linkBtn} onClick={handleItems}>
                  Show all product
                </button>
              </span>
            </div>
          </div>
        </div>
        <div className={styles.rule} />
        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <span className={styles.eyebrow}>Customer Details</span>
            <button type="button" className={`${styles.pillBtn} ${styles.pillSm}`} onClick={handleCustomerDetail}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              Show Details
            </button>
          </div>
          <div className={styles.people}>
            <div className={styles.person}>
              <span className={styles.tileLabel}>Name</span>
              <span className={styles.tileValue}>{censor(`${shipping?.name} ${shipping?.name}`)}</span>
            </div>
            <div className={styles.person}>
              <span className={styles.tileLabel}>Phone Number</span>
              <span className={styles.tileValue}>{addressphone(shipping?.msisdn)}</span>
            </div>
            <div className={styles.person}>
              <span className={styles.tileLabel}>Address</span>
              <span className={styles.tileValue}>{addressphone(shipping?.address)}</span>
            </div>
          </div>
        </div>
      </div>

      {itemListModal && (
        <Items data={trackdata} close={() => setItemListModal(false)} />
      )}
      {displayOtpPage && (
        <Otp
          setActivestatus={""}
          setItemListModal={setItemListModal}
          action={action}
          setotpPage={setotpPage}
          setDelivery={setDelivery}
          setDeliveryOtp={(value) => {
            // OTP verified in the modal: show the delivery OTP right away, as after a session fetch.
            setDeliveryOtp(value);
            setOtpRevealed(true);
          }}
          subOrderId={trackdata?.orderDetail?.[0]?.suborder_id}
          orderInfo={trackdata?.orderDetail?.[0]?.order}
          awb={shipment?.awbnumber}
          activeStatus={""}
          reschedule={""}
          close={() => setotpPage(false)}
        />
      )}
      {openOrderStatus && (
        <DeliveryUpdatesModal trackingData={events} steps={finalArray ?? []} shipmentStatus={shipmentstatus} onClose={closeOrderStatus} />
      )}
    </div>
  );
};

export default TrackingMainScreen;
